import { useEffect, useState } from 'react';
import Tree from 'react-d3-tree';
import { ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Pencil, Plus, Trash2, ArrowUp } from 'lucide-react';
import ConfirmDeleteModal from '@/components/company/table/ConfirmDeleteModal';

export default function OrgChartTree({ isLoading, treeData, onAddChild, onAddLeftSibling, onAddRightSibling, onAddTopNode, onEdit, onSelect, onDelete, viewMode, exportMode = false, selectedNodeId }: any) {
  const [zoom, setZoom] = useState(1.0);
  const [translate, setTranslate] = useState({ x: window.innerWidth / 2, y: 100 });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedDeleteNode, setSelectedDeleteNode] = useState<any>(null);

  useEffect(() => {
    if (exportMode) {
      // Zoom out and center for export
      setZoom(0.5);
      setTranslate({ x: 1000, y: 100 });
      return;
    }

    if (viewMode === 'diagram') {
      setZoom(1.3);
      setTranslate({ x: window.innerWidth / 2, y: 100 });
    } else if (viewMode === 'split') {
      setZoom(1.0);
      setTranslate({ x: window.innerWidth / 3, y: 100 });
    } else {
      setZoom(1.0);
      setTranslate({ x: window.innerWidth / 2, y: 100 });
    }
  }, [viewMode, exportMode]);

  const renderCustomNode = ({ nodeDatum, toggleNode, hierarchyPointNode }: any) => {
    const childCount = nodeDatum.children?.length || 0;
    const isRoot = hierarchyPointNode.depth === 0;

    return (
      <g
        onClick={() => {
          if (onSelect && !exportMode) onSelect(nodeDatum);
        }}
        className={exportMode ? "" : "cursor-pointer"}
      >
        {/* Main node rectangle */}
        <rect
          width="180"
          height="45"
          x="-90"
          y="-22.5"
          rx="8"
          ry="8"
          fill={selectedNodeId === nodeDatum.id ? "#eff6ff" : "#fff"}
          stroke={selectedNodeId === nodeDatum.id ? "#2563eb" : "#9E9E9E"}
          strokeWidth={selectedNodeId === nodeDatum.id ? 2.5 : 1}
          className="transition-all duration-300"
        />

        {/* Node content */}
        <foreignObject x="-85" y="-20" width="170" height="40">
          <div className="flex items-center justify-between h-full px-2 text-[12px] text-slate-800 font-medium">
            <div className="flex items-center gap-1">
              {!exportMode && childCount > 0 && (
                <button
                  onClick={e => {
                    e.stopPropagation();
                    toggleNode();
                  }}
                >
                  {nodeDatum.__rd3t.collapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
                </button>
              )}
              <span className="break-words max-w-[100px] leading-tight">{nodeDatum.name}</span>
              {childCount > 0 && (
                <span className="ml-1 px-1.5 py-0.5 rounded-md bg-slate-200 text-xs">{childCount}</span>
              )}
            </div>

            {/* Action buttons inside node - Edit & Delete */}
            {!exportMode && (
              <div className="flex items-center gap-1">
                <button
                  title="Edit"
                  className="text-primary hover:text-primary/70"
                  onClick={e => {
                    e.stopPropagation();
                    onEdit(nodeDatum);
                  }}
                >
                  <Pencil size={12} strokeWidth={2.5} />
                </button>
                {!isRoot && (
                  <button
                    title="Delete"
                    className="text-red-500 hover:text-red-400 transition"
                    onClick={e => {
                      e.stopPropagation();
                      setSelectedDeleteNode(nodeDatum);
                      setDeleteModalOpen(true);
                    }}
                  >
                    <Trash2 size={12} strokeWidth={2.5} />
                  </button>
                )}
              </div>
            )}
          </div>
        </foreignObject>

        {/* Floating action buttons - subtle, positioned on node border */}
        {!exportMode && (
          <>
            {/* LEFT SIBLING - centered on left border */}
            {!isRoot && (
              <foreignObject x="-97" y="-7" width="14" height="14">
                <button
                  title="Add Left Sibling"
                  className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-slate-400 text-white hover:bg-blue-500 transition-all opacity-60 hover:opacity-100"
                  onClick={e => {
                    e.stopPropagation();
                    onAddLeftSibling(nodeDatum);
                  }}
                >
                  <ChevronLeft size={10} strokeWidth={2.5} />
                </button>
              </foreignObject>
            )}

            {/* RIGHT SIBLING - centered on right border */}
            {!isRoot && (
              <foreignObject x="83" y="-7" width="14" height="14">
                <button
                  title="Add Right Sibling"
                  className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-slate-400 text-white hover:bg-blue-500 transition-all opacity-60 hover:opacity-100"
                  onClick={e => {
                    e.stopPropagation();
                    onAddRightSibling(nodeDatum);
                  }}
                >
                  <ChevronRight size={10} strokeWidth={2.5} />
                </button>
              </foreignObject>
            )}

            {/* ADD CHILD - centered on bottom border */}
            <foreignObject x="-7" y="15" width="14" height="14">
              <button
                title="Add Child"
                className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-slate-400 text-white hover:bg-green-500 transition-all opacity-60 hover:opacity-100"
                onClick={e => {
                  e.stopPropagation();
                  onAddChild(nodeDatum);
                }}
              >
                <Plus size={10} strokeWidth={2.5} />
              </button>
            </foreignObject>

            {/* ADD TOP NODE - centered on top border (root only) */}
            {isRoot && (
              <foreignObject x="-7" y="-30" width="14" height="14">
                <button
                  title="Add Top Node"
                  className="w-3.5 h-3.5 flex items-center justify-center rounded-full bg-slate-400 text-white hover:bg-purple-500 transition-all opacity-60 hover:opacity-100"
                  onClick={e => {
                    e.stopPropagation();
                    onAddTopNode(nodeDatum);
                  }}
                >
                  <ArrowUp size={10} strokeWidth={2.5} />
                </button>
              </foreignObject>
            )}
          </>
        )}
      </g>
    );
  };

  if (isLoading) {
    return (
      <div className="mt-20 flex justify-center">
        <p className="text-gray-400 text-sm mt-5">Loading organization chart...</p>
      </div>
    );
  }

  return (
    <div className="flex justify-center overflow-x-auto transition-all duration-300">
      {!treeData || treeData.length === 0 ? (
        <p className="text-gray-400 text-sm mt-5">No org chart found.</p>
      ) : (
        <div style={{ width: '100%', height: exportMode ? '2000px' : '70vh' }}>
          <Tree
            data={treeData}
            orientation="vertical"
            translate={translate}
            pathFunc="step"
            renderCustomNodeElement={renderCustomNode}
            separation={{ siblings: 1.5, nonSiblings: 1.5 }}
            zoom={zoom}
            scaleExtent={{ min: 0.5, max: 2 }}
          />
        </div>
      )}

      <ConfirmDeleteModal
        open={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setSelectedDeleteNode(null);
        }}
        onConfirm={() => {
          if (selectedDeleteNode && onDelete) {
            onDelete(selectedDeleteNode);
          }
          setDeleteModalOpen(false);
          setSelectedDeleteNode(null);
        }}
        itemName={selectedDeleteNode?.name}
        childCount={selectedDeleteNode?.children?.length || 0}
      />
    </div>
  );
}
